import { invariantResponse } from '@epic-web/invariant'
import {
	json,
	type MetaFunction,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { StarRating } from '#app/components/star-rating.js'
import { SITE_FULL_TITLE } from '#app/utils/client-constants.js'
import { prisma } from '#app/utils/db.server.js'
import { getProductImgSrc } from '#app/utils/misc.js'
import defaultProductImage from '/img/image.png'

export async function loader({ params }: LoaderFunctionArgs) {
	const user = await prisma.user.findFirst({
		where: {
			username: params.username,
		},
		select: {
			id: true,
			name: true,
			reviews: {
				orderBy: {
					createdAt: 'desc',
				},
				select: {
					id: true,
					createdAt: true,
					rating: true,
					content: true,
					product: {
						select: {
							id: true,
							name: true,
							slug: true,
							category: { select: { slug: true } },
							images: {
								select: {
									id: true,
									altText: true,
								},
							},
						},
					},
				},
			},
		},
	})

	invariantResponse(user, 'User not found', { status: 404 })

	return json({
		user,
	})
}

export default function ReviewsAdded() {
	const data = useLoaderData<typeof loader>()
	const reviewsAddedCount = data.user.reviews.length

	return (
		<div className="flex flex-col gap-12">
			<div className="flex w-full items-center justify-between">
				<h5 className="text-lg text-foreground">
					<span className="font-bold">{reviewsAddedCount}</span>{' '}
					<span>{reviewsAddedCount === 1 ? 'review' : 'reviews'} added</span>
				</h5>
			</div>
			<div>
				<ul className="flex w-full flex-col items-start justify-start gap-4 delay-200">
					{data.user.reviews.map(review => (
						<li key={review.id} className="w-full">
							<Link
								to={`/products/${review.product.category.slug}/${review.product.id}/${review.product.slug}`}
								className="flex w-full items-center gap-4 rounded-md bg-muted"
							>
								<img
									alt={review.product.images[0]?.altText ?? 'Product image'}
									src={
										review.product.images[0]?.id
											? getProductImgSrc(review.product.images[0].id)
											: defaultProductImage
									}
									className="h-24 w-24 rounded-xl rounded-r-none object-cover"
								/>
								<div className="flex w-full flex-col overflow-hidden">
									<span className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-left font-bold max-sm:text-xs">
										{review.product.name}
									</span>
									<StarRating
										rating={review.rating}
										clickable={false}
										size="sm"
									/>
									<span className="line-clamp-2 w-full text-left text-muted-foreground max-sm:text-xs">
										{review.content}
									</span>
								</div>
							</Link>
						</li>
					))}
				</ul>
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	const contentTitle = 'Reviews Added'

	return [
		{ title: `${displayName}'s ${contentTitle} | ${SITE_FULL_TITLE}` },
		{
			name: 'description',
			content: `See ${displayName}'s ${contentTitle} on ${SITE_FULL_TITLE}`,
		},
	]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
